import { useCallback, useState } from "react";
import {
  useQueryClient,
  UseInfiniteQueryResult,
  InfiniteData,
} from "@tanstack/react-query";
import { DisplayProjectStore, WriteProjectStore } from "@/types/projectStore";
import { SuccessFetchListResponse } from "@/services/api";
import { Module } from "@/constants";
import { ProjectStoreService } from "../service/projectStore";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useTranslation } from "react-i18next";

interface UseProjectStoresAddOptions {
  query: UseInfiniteQueryResult<
    InfiniteData<SuccessFetchListResponse<DisplayProjectStore>>
  >;
  projectId: string;
  onSuccess: () => void;
}

export const useProjectStoresAdd = ({
  query,
  projectId,
  onSuccess,
}: UseProjectStoresAddOptions) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mutation = useMutationWithErrorHandling({
    mutationFn: (values: WriteProjectStore) =>
      ProjectStoreService.patch(projectId, values, Module.Stores),
    errorTitle: t("projectStores.errors.updateProject"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["projectStorePeriods"],
        refetchType: "all",
      });
      query.refetch();
      onSuccess();
    },
  });

  const handleSubmit = useCallback(
    async (selectedIds: string[]) => {
      if (selectedIds.length === 0) return;

      setIsSubmitting(true);
      try {
        await mutation.mutateAsync({
          add: selectedIds,
          delete: [],
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [mutation],
  );

  return {
    handleSubmit,
    isSubmitting: isSubmitting || mutation.isPending,
  };
};
